/* ============================================================
   A hotkey de reexibir o launcher — a única tecla do widget, e a que precisa
   ganhar de um host DESCONHECIDO.

   Mora fora do WidgetUI de propósito. Até o DEV-322 ela era registrada no
   CONSTRUTOR do widget, e o `index.ts` só instancia o widget depois do
   `await buscarConfigRemota(...)`: a prioridade do Relator dependia de um
   round-trip de rede terminar, e qualquer host que registrasse nesse intervalo
   ganhava. Aqui ela é ligada no escopo de módulo, antes do bootstrap.

   ATÉ ONDE VAI A GARANTIA, escrito para ninguém prometer mais do que é:
   ganhamos de listener em borbulha (a maioria), de delegação de framework, de
   `document` e de captura registrada DEPOIS do nosso script — que é o caso de
   todo host realista, já que o script do widget entra cedo. Continuamos
   perdendo de: captura no `window` registrada ANTES de nós, atalho do host em
   `keyup`, evento dentro de um `<iframe>` do host, e atalho do browser ou do
   sistema. A saída absoluta seria mover a UI para um <iframe>, e o preço é
   orquestrar o `modern-screenshot` — que rasteriza o DOM do HOST — de fora.
   É por isso que a tecla é CONFIGURÁVEL por relator: quando o host reivindica
   a nossa, troca-se a nossa.
   ============================================================ */

/** A tecla padrão. `?` é a que sempre foi, e continua sendo (decisão do dono do
    produto, 02/09/2026) — o que o DEV-322 muda é o MECANISMO em volta dela.

    A objeção mais forte ao `?` caiu junto: ele colidia com a ajuda de atalhos do
    próprio app — o Tasks embarca o próprio Relator, e um `?` escondia o launcher
    E abria a ajuda ao mesmo tempo — e essa ajuda deixou de existir.

    As duas que FICAM, escritas para quem for reabrir o assunto: `?` é caractere
    IMPRIMÍVEL, então o guard de digitação o mata exatamente no host com campo de
    busca autofocado, que é justo onde ele mais faria falta; e ele nasce de
    `Shift+/` na maioria dos layouts, o que o torna difícil em teclado que ponha
    o `?` atrás de outro modificador. Para os dois a saída é a mesma, e por isso
    o combo é CONFIGURÁVEL: `data-altbug-hotkey-combo` no host, ou `hotkeyCombo`
    no `init`. */
export const HOTKEY_PADRAO = "?";

/** Onde o combo em vigor é publicado, para o host CEDER a tecla sem saber quem
    somos nem o que ela faz. É o único mecanismo determinístico na página do
    próprio Tasks: o `RelatorWidget` injeta o <script> dentro de um efeito, então
    o despachante do app registra ANTES do widget e venceria a corrida. */
export const ATRIBUTO_TECLAS = "data-altbug-teclas";

const NOMEADAS = new Set([
  "Escape", "Enter", "Tab", "Backspace", "Delete", "Space",
  "Home", "End", "PageUp", "PageDown",
  "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight",
]);

/** Combo canônico do evento. Precisa produzir EXATAMENTE o mesmo texto que o
    `combo()` de `web/lib/teclado/combo.ts`: o widget publica o combo em vigor e
    o app o compara com o que calcula do MESMO keydown para ceder a tecla. Se as
    duas gramáticas divergirem, a cessão para de casar — e o sintoma é o app
    disparando por cima do widget, sem erro em canto nenhum. Há teste de
    paridade (`hotkey.contrato.test.ts`).

    As três regras, e cada uma tem motivo:
    - ⌘ e Ctrl colapsam em `Mod` (a diferença é de exibição, não de identidade);
    - com Alt a identidade vem do `code`: no macOS `Alt+B` produz `key` "∫";
    - Shift só entra quando NÃO está no caractere. `Shift+/` já chega como "?",
      e escrever "Shift+?" contaria o modificador duas vezes — é o que faz o
      padrão `?` casar. Mas `Shift+x` chega como "X", que minusculado colidiria
      com "x", então para LETRA o Shift entra. */
function comboDe(e: KeyboardEvent): string {
  const partes: string[] = [];
  if (e.metaKey || e.ctrlKey) partes.push("Mod");
  if (e.altKey) partes.push("Alt");

  let base = e.key === " " ? "Space" : e.key;
  if (e.altKey) {
    const m = /^Key([A-Z])$/.exec(e.code);
    base = m?.[1]?.toLowerCase() ?? (/^Digit([0-9])$/.exec(e.code)?.[1] ?? base);
  }

  const letra = base.length === 1 && /\p{L}/u.test(base);
  if (e.shiftKey && (letra || NOMEADAS.has(base))) partes.push("Shift");
  partes.push(letra ? base.toLowerCase() : base);
  return partes.join("+");
}

type Alvo = { alternarLauncher: () => void; temOverlayAberto: () => boolean } | null;

let alvo: Alvo = null;
let combo = HOTKEY_PADRAO;
let ligado = false;

const onKeydown = (e: KeyboardEvent): void => {
  // IME em composição e auto-repeat não são comando.
  if (e.isComposing || e.repeat) return;
  if (comboDe(e) !== combo) return;
  if (!alvo || alvo.temOverlayAberto()) return;

  // O alvo REAL, atravessando shadow: `e.target` cru veria o host e o widget
  // agiria enquanto se digita num host de shadow ABERTO.
  const cru = (e.composedPath?.()[0] ?? e.target) as unknown;
  if (cru instanceof HTMLElement) {
    if (cru.isContentEditable || /^(input|textarea|select)$/i.test(cru.tagName)) return;
  }

  e.preventDefault();
  // `stopImmediatePropagation` e não só `stopPropagation`: o segundo não barra
  // listener IRMÃO no mesmo alvo e na mesma fase, que é justamente o host que
  // também escuta em window/captura. Sem isto, ganhar a ordem não impedia o host
  // de agir — era o furo do handler antigo, que só chamava preventDefault.
  e.stopImmediatePropagation();
  alvo.alternarLauncher();
};

/** Liga o atalho. Idempotente e chamável ANTES de existir widget: o alvo entra
    depois, por `apontarHotkey`. */
export function ligarHotkey(): void {
  if (ligado || typeof window === "undefined") return;
  window.addEventListener("keydown", onKeydown, true);
  ligado = true;
}

/** Quem a tecla aciona, e com que combo. `null` desarma sem desligar o listener
    (o widget pode ser destruído e recriado). */
export function apontarHotkey(novo: Alvo, comboConfigurado?: string | null): void {
  alvo = novo;
  combo = comboConfigurado?.trim() || HOTKEY_PADRAO;
  if (typeof document === "undefined") return;
  const raiz = document.documentElement;
  if (novo) raiz.setAttribute(ATRIBUTO_TECLAS, combo);
  else raiz.removeAttribute(ATRIBUTO_TECLAS);
}

export function desligarHotkey(): void {
  if (!ligado || typeof window === "undefined") return;
  window.removeEventListener("keydown", onKeydown, true);
  ligado = false;
  apontarHotkey(null);
}

const SIMBOLO: Record<string, string> = { Mod: "⌘", Alt: "⌥", Shift: "⇧" };

/** O combo como se escreve num <kbd>. O diálogo de esconder PROMETE esta tecla
    na tela — imprimir um literal foi o que fez a promessa envelhecer errada. */
export function exibirHotkey(c: string): string {
  return (c || HOTKEY_PADRAO)
    .split("+")
    .map((p) => SIMBOLO[p] ?? (p.length === 1 ? p.toUpperCase() : p))
    .join("");
}
